import { saveAuthSession } from './authSession';

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

function unwrapError(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload;
  return (
    payload.message ||
    payload.error ||
    payload.errors?.[0]?.message ||
    fallback
  );
}

function normalizeUser(data) {
  const user =
    data?.user ||
    data?.data?.user ||
    data?.data ||
    (data?.id || data?.user_id ? data : null);
  if (!user || typeof user !== 'object') return null;
  const id = user.id || user.user_id || user.userId;
  if (!id) return null;
  return { ...user, id };
}

function sessionFromPayload(payload, fallbackError) {
  const user = normalizeUser(payload);
  const token =
    payload?.access_token ||
    payload?.token ||
    payload?.data?.access_token ||
    '';
  if (!user) {
    throw new Error(unwrapError(payload, fallbackError));
  }
  return saveAuthSession({ user, token });
}

async function postJson(path, body) {
  if (!apiBaseUrl) throw new Error('API base URL is not set. Check REACT_APP_API_BASE_URL in .env');
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(unwrapError(payload, `Request failed with status ${response.status}`));
  }
  return payload;
}

export async function loginUser({ email, password }) {
  const payload = await postJson('/auth/user/login', { email, password });
  return sessionFromPayload(payload, 'Sign in succeeded but no user was returned.');
}

export async function registerUser(values) {
  const phone =
    values.phone_number ||
    (values.phoneNumber ? normalizePhoneNumber(values.phoneNumber) : '');
  const body = {
    email: String(values.email || '').trim().toLowerCase(),
    first_name: values.first_name || values.firstName,
    last_name: values.last_name || values.lastName,
    username: values.username,
    password: values.password,
    confirm_password: values.confirm_password || values.confirmPassword,
  };
  if (phone) body.phone_number = phone;
  const payload = await postJson('/auth/user/register', body);
  if (payload?.access_token || payload?.token || payload?.data?.access_token) {
    try {
      return sessionFromPayload(payload, 'Account created but no user was returned.');
    } catch {
      /* fall through */
    }
  }
  return payload;
}

/** Returns true if the number is free for signup. */
export async function preSignupCheckPhone({ phone_number }) {
  const payload = await postJson('/auth/user/pre-signup/check-phone', {
    phone_number,
  });
  const taken =
    payload?.exists === true ||
    payload?.phone_exists === true ||
    payload?.in_use === true ||
    payload?.available === false;
  if (taken) {
    throw new Error(
      unwrapError(payload, 'That phone number is already in use.')
    );
  }
  return true;
}

export async function verifyUserEmail({ email, verification_code }) {
  return postJson('/auth/user/verify', { email, verification_code });
}

export async function forgotPassword({ email }) {
  return postJson('/auth/user/forgot-password', { email });
}

export async function verifyResetCode({ email, verification_code }) {
  return postJson('/auth/verify-code', { email, verification_code });
}

export async function resetPassword({ user_id, new_password, confirm_password }) {
  return postJson('/auth/user/reset-password', {
    user_id,
    new_password,
    confirm_password,
  });
}

export async function sendPhoneVerification({ phone_number, purpose }) {
  return postJson('/auth/user/phone-verification/send', { phone_number, purpose });
}

export async function verifyPhoneVerification({ phone_number, code }) {
  return postJson('/auth/user/phone-verification/verify', { phone_number, code });
}

export async function loginWithPhone({ phone_number }) {
  const payload = await postJson('/auth/user/login-with-phone', { phone_number });
  return sessionFromPayload(payload, 'Sign in succeeded but no user was returned.');
}

export function normalizePhoneNumber(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  const body = trimmed.startsWith('+') ? trimmed.slice(1) : trimmed;
  const digits = body.replace(/\D/g, '');
  if (!digits) return '';
  return `+${digits}`;
}

export function isPhoneValidForOtp(normalized) {
  if (!normalized.startsWith('+')) return false;
  return normalized.slice(1).replace(/\D/g, '').length >= 10;
}

export function allowOnlyOtpDigits(next, maxLen = 6) {
  return String(next || '').replace(/\D/g, '').slice(0, maxLen);
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
}

export function extractUserId(payload) {
  return (
    payload?.user_id ||
    payload?.userId ||
    payload?.user?.id ||
    payload?.user?.user_id ||
    payload?.data?.user_id ||
    payload?.data?.user?.id ||
    payload?.id ||
    ''
  );
}
